// ABOUTME: Hook for managing PWA install prompt state
// ABOUTME: Tracks beforeinstallprompt event and handles install flow

import { useState, useEffect } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

interface UsePWAInstallReturn {
  canInstall: boolean
  isInstalled: boolean
  promptInstall: () => Promise<boolean>
  dismissPrompt: () => void
  shouldShowPrompt: boolean
}

const VISIT_COUNT_KEY = 'gentleman-visit-count'
const PROMPT_DISMISSED_KEY = 'gentleman-install-dismissed'
const MIN_VISITS_BEFORE_PROMPT = 2

export function usePWAInstall(): UsePWAInstallReturn {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setIsInstalled] = useState(false)
  const [visitCount, setVisitCount] = useState(0)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true)
      return
    }

    // Track visit count
    const stored = localStorage.getItem(VISIT_COUNT_KEY)
    const count = stored ? parseInt(stored, 10) + 1 : 1
    localStorage.setItem(VISIT_COUNT_KEY, String(count))
    setVisitCount(count)

    // Check if dismissed
    const wasDismissed = localStorage.getItem(PROMPT_DISMISSED_KEY) === 'true'
    setDismissed(wasDismissed)

    // Listen for install prompt
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }

    // Listen for successful install
    const handleAppInstalled = () => {
      setIsInstalled(true)
      setDeferredPrompt(null)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstall)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const promptInstall = async (): Promise<boolean> => {
    if (!deferredPrompt) return false

    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice

    if (outcome === 'accepted') {
      setIsInstalled(true)
    }

    setDeferredPrompt(null)
    return outcome === 'accepted'
  }

  const dismissPrompt = () => {
    localStorage.setItem(PROMPT_DISMISSED_KEY, 'true')
    setDismissed(true)
  }

  const canInstall = deferredPrompt !== null && !isInstalled
  const shouldShowPrompt = canInstall && visitCount >= MIN_VISITS_BEFORE_PROMPT && !dismissed

  return {
    canInstall,
    isInstalled,
    promptInstall,
    dismissPrompt,
    shouldShowPrompt,
  }
}
